'use client';

import AgsForm from 'components/AgsForm';
import Alternates from 'components/Alternates';
import ContentPageSlots from 'components/ContentPageSlots';
import DbsCompetitionForm from 'components/DbsCompetitionForm';
import Gap from 'components/Gap';
import Header from 'components/Header';
import { getOrigin } from 'lib/util';
import type { MediaSource } from 'models/contentful/media/types';
import type { ContentPage as ContentPageModel } from 'models/contentPages/types';
import { usePathname } from 'next/navigation';
import styled from 'styled-components';

const ContentPageHeader = styled(Header)`
  padding: 48px 0;
`;

const ContentContainer = styled.div`
  :has([data-overlay='true']) & {
    position: relative;
    margin-top: -80px;
  }
`;

export default function ContentPage({
  header,
  displayMode,
  darkMode,
  slots,
}: Omit<
  ContentPageModel,
  'browserTitle' | 'metaTitle' | 'metaDescription' | 'metaImage'
>) {
  const pathname = usePathname();

  const pagePath: string | undefined = pathname.split('/').filter(Boolean)[2];

  let imagesToPreload: string[] = [];

  if (slots[0] && slots[0].blockType === 'Hero') {
    imagesToPreload = Object.values(slots[0].storySource?.media ?? {}).reduce(
      (acc: string[], value) => {
        if (value) {
          const mediaSource = value as unknown as MediaSource;
          if (!acc.includes(mediaSource.image.url)) {
            acc.push(mediaSource.image.url);
          }
        }
        return acc;
      },
      []
    );
  }

  return (
    <>
      <link rel="canonical" href={`${getOrigin()}${pathname}`} />
      <Alternates pathname={pathname} />
      {imagesToPreload.map(url => (
        <link key={url} rel="preload" href={url} as="image" />
      ))}
      <div
        id="layout"
        data-dark={displayMode === 'overlay' && darkMode}
        data-overlay={displayMode === 'overlay'}
      />
      <ContentContainer>
        {header && (
          <>
            <ContentPageHeader
              {...header}
              headerLevel="h1"
              headerVariant="2"
              truncate
            />
            <Gap size={{ xs: 24, l: 48 }} />
          </>
        )}
        <main id="main-content" tabIndex={-1}>
          <ContentPageSlots slots={slots} displayMode={displayMode} />
          <AgsForm visible={pagePath?.startsWith('perfect-moment-ags')} />
          <DbsCompetitionForm
            visible={pagePath?.startsWith('dbs-competition')}
          />
        </main>
      </ContentContainer>
    </>
  );
}
